import { AnalyticsEvent } from "@repo/edtech-analytics";

import { GlobalPlayerStore, PlayerActions } from "../../types";
import { DEFAULT_STATE } from "../create-player-store";

export const createPlayerSlice = (store: GlobalPlayerStore): PlayerActions => {
  let gestureBound = false;

  const tryPlayIfPending = () => {
    if (store.get().pendingPlayByUser) store.playerActions.play();
  };

  const bindFirstGesture = () => {
    if (gestureBound) return;
    gestureBound = true;

    const onAny = () => {
      tryPlayIfPending();
      document.removeEventListener("pointerdown", onAny);
      document.removeEventListener("keydown", onAny);
    };

    document.addEventListener(
      "pointerdown", onAny, {
        once: true,
      },
    );
    document.addEventListener(
      "keydown", onAny, {
        once: true,
      },
    );
  };

  const setLoading = (v: boolean) => {
    const s = store.get();
    if (s.isLoading !== v) store.set({
      isLoading: v,
    });
  };

  return {
    getPlayerElement: (): HTMLMediaElement | null => store.get().playerEl ?? null,

    setPlayerEl: (el: HTMLMediaElement | null) => {
      if (!el) return;

      if (store.get().playerEl !== el) {
        store.set({
          playerEl: el,
        });
      }

      const getTracker = () => store.get().analyticsTracker;

      el.muted = store.get().muted;

      const MAX_DURATION_RECALC_ATTEMPTS = 3;

      const forceDurationRecalc = (attempt = 0) => {
        if (Number.isFinite(el.duration)) return;
        if (attempt >= MAX_DURATION_RECALC_ATTEMPTS) return;

        const prevTime = el.currentTime;

        const onSeeked = () => {
          el.removeEventListener("seeked", onSeeked);
          el.currentTime = prevTime;
          store.set({
            duration: el.duration,
          });

          if (!Number.isFinite(el.duration)) {
            const retry = () => {
              el.removeEventListener("progress", retry);
              el.removeEventListener("canplaythrough", retry);
              forceDurationRecalc(attempt + 1);
            };

            el.addEventListener(
              "progress", retry, {
                once: true,
              },
            );
            el.addEventListener(
              "canplaythrough", retry, {
                once: true,
              },
            );
          }
        };

        el.addEventListener("seeked", onSeeked);
        el.currentTime = Number.MAX_SAFE_INTEGER;
      };

      el.ontimeupdate = () => store.set({
        currentTime: el.currentTime,
      });
      el.onloadedmetadata = () => {
        store.set({
          duration: el.duration,
        });
        forceDurationRecalc();
      };
      el.ondurationchange = () => store.set({
        duration: el.duration,
      });

      el.onplay = () => {
        store.set({
          playing: true,
          ended: false,
          pendingPlayByUser: false,
        });

        const track = getTracker();
        if (track && store.get().source) {
          const resumed = el.currentTime > 0;
          track({
            type: resumed ?
              AnalyticsEvent.Resumed :
              AnalyticsEvent.Started,
            time: el.currentTime,
          },
          !resumed);
        }
      };

      el.onpause = () => {
        store.set({
          playing: false,
        });

        const track = getTracker();
        if (track) {
          track({
            type: AnalyticsEvent.Paused,
            time: el.currentTime,
          });
        }
      };

      el.onended = () => {
        store.set({
          playing: false,
          ended: true,
        });

        const track = getTracker();
        if (track) {
          track(
            {
              type: AnalyticsEvent.Completed,
              time: el.currentTime,
            },
            true, true,
          );
        }
      };

      el.onwaiting = () => setLoading(true);
      el.onseeking = () => setLoading(true);
      el.onloadstart = () => setLoading(true);

      el.oncanplay = () => {
        store.set({
          isLoading: false,
        });
        tryPlayIfPending();
      };
      el.onplaying = () => setLoading(false);
      el.oncanplaythrough = () => setLoading(false);
      el.onseeked = () => setLoading(false);

      tryPlayIfPending();
    },

    play: () => {
      const el = store.playerActions.getPlayerElement();
      if (!el) return;

      el.play().catch((e: any) => {
        if (e?.name === "NotAllowedError") {
          store.set({
            pendingPlayByUser: true,
          });
          bindFirstGesture();
        }
      });
    },

    pause: () => {
      const el = store.playerActions.getPlayerElement();
      if (el) el.pause();
    },

    toggle() {
      const el = store.playerActions.getPlayerElement();
      if (!el) return;

      if (el.paused) {
        store.set({
          pendingPlayByUser: true,
        });
        store.playerActions.play();
      } else {
        el.pause();
      }
    },

    seekTo: (time) => {
      const el = store.playerActions.getPlayerElement();
      if (el) {
        el.currentTime = time;
        store.set({
          currentTime: time,
          ended: false,
        });
      }
    },

    reset: () => {
      const el = store.playerActions.getPlayerElement();
      if (el) {
        el.pause();
        el.removeAttribute("src");
        el.load();
      }

      const prev = store.get();
      store.set({
        ...DEFAULT_STATE,
        playerEl: prev.playerEl,
        playerWrapEl: prev.playerWrapEl,
        analyticsTracker: prev.analyticsTracker,
        speed: prev.speed,
        muted: prev.muted,
        isMobile: prev.isMobile,
      });
    },

    setSpeed: (speed) => {
      const el = store.playerActions.getPlayerElement();
      if (el) el.playbackRate = parseFloat(speed.replace(",", "."));
      store.set({
        speed,
      });
    },

    setMobile: (isMobile: boolean) => {
      store.set({
        isMobile,
      });
    },

    setActivate: ({
      id,
      source,
      title,
      clipMode = false,
      autoplay = false,
    }) => {
      const prev = store.get();
      const el = store.playerActions.getPlayerElement();

      if (el) {
        el.pause();
        el.removeAttribute("src");
        el.load();
      }
      const hasSource = !!source;

      const shouldAutoplay = hasSource && (autoplay || clipMode);

      store.set({
        ...DEFAULT_STATE,
        playerEl: prev.playerEl,
        analyticsTracker: prev.analyticsTracker,
        isMobile: prev.isMobile,
        id,
        source,
        title,
        clipMode,
        muted: clipMode,
        pendingPlayByUser: shouldAutoplay,
        ended: false,
        isLoading: false,
      });
      if (el) {
        el.muted = clipMode ?
          true :
          prev.muted;
        el.setAttribute("src", source);

        if (shouldAutoplay) {
          el.load();
          store.playerActions.play();
        } else {
          el.load();
        }
      }
    },

    setFileSize: (size: number) => {
      store.set({
        fileSize: size,
      });
    },

    setAnalyticsTracker: (track) => {
      store.set({
        analyticsTracker: track,
      });
    },

    setMuted: (muted: boolean) => {
      store.set({
        muted,
      });
      const el = store.playerActions.getPlayerElement();
      if (el) el.muted = muted;
    },
  };
};
